import { skipToken } from '@reduxjs/toolkit/query/react'
import { useFetchInfluencerSimilarKolsQuery } from '@/api/influencer-api'
import { useFetchKolsSimilarKolsQuery } from '@/api/k6-kols-api'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchSimilarKolsQuery } from '@/api/workspace-api/kol-api'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import { mockSimilarKolList } from '@/constants/mock-data/similar-kols'
import useAgentInfo from '@/hooks/use-agent-info'
import useIsPublicDetailPage from '@/hooks/use-is-public-detail-page'
import { usePermissions } from '@/hooks/use-permissions'
import { SimilarKols } from '@/store/kol-detail'
import { ListResource } from '@/types/resource-type'
import UseQueryResult from '@/types/rtk-query'
import { KolUserIdentityType } from '@/types/schema/agent-schema'
import { PlatformShortcode } from '@/utils/convert-platform'

const defaultPage = 1
const defaultPerPage = 6

const similarMaxViewCountByIdentity: Record<KolUserIdentityType, number> = {
  [KolUserIdentityType.KOL]: 6,
  [KolUserIdentityType.RadarStarKOL]: 12,
  [KolUserIdentityType.Agency]: 6,
  [KolUserIdentityType.RadarStarAgency]: 12,
  [KolUserIdentityType.AgencyKOL]: 6,
  [KolUserIdentityType.RadarStarAgencyKOL]: 0,
}

type UseSimilarKols = (params: {
  kolId: string
  kolDcIdHashed?: string
  platform: PlatformShortcode
}) => UseQueryResult<ListResource<SimilarKols>>

const useSimilarKols: UseSimilarKols = ({ kolId, kolDcIdHashed, platform }) => {
  const { isLocked } = useKolInfo(kolId, kolDcIdHashed)
  const { kolUserIdentityType, radarStarAgencyKOL } = useAgentInfo()
  const isPublicDetailPage = useIsPublicDetailPage()
  const { withKolPermission } = usePermissions()
  const { data: userStatus } = useFetchUserStatusQuery()

  const per_page = ((): number => {
    // ad 預設 12 筆
    if (!withKolPermission && !isPublicDetailPage) {
      return 12
    }

    // kol 看權限
    if (withKolPermission && kolUserIdentityType) {
      return similarMaxViewCountByIdentity[kolUserIdentityType]
    }

    return defaultPerPage
  })()

  const similarKolsByAd = useFetchSimilarKolsQuery(
    !withKolPermission &&
      userStatus?.currentWorkspaceId &&
      kolId &&
      platform &&
      !isPublicDetailPage
      ? {
          workspaceId: userStatus.currentWorkspaceId,
          radar_uuid: kolId,
          dc_id_hashed: kolDcIdHashed,
          platform,
          page: defaultPage,
          per_page,
        }
      : skipToken,
  )

  const similarKolsByKol = useFetchInfluencerSimilarKolsQuery(
    withKolPermission &&
      kolUserIdentityType &&
      !radarStarAgencyKOL &&
      kolId &&
      platform &&
      !isPublicDetailPage
      ? {
          uuid: kolId,
          platform,
          page: defaultPage,
          per_page,
        }
      : skipToken,
  )

  const similarKolsByPublic = useFetchKolsSimilarKolsQuery(
    isPublicDetailPage && kolId && platform
      ? {
          uuid: kolId,
          platform,
          page: defaultPage,
          per_page: 1,
        }
      : skipToken,
  )

  const similarKols = ((): UseQueryResult<ListResource<SimilarKols>> => {
    if (isPublicDetailPage) {
      return similarKolsByPublic
    }

    if (!withKolPermission) {
      return similarKolsByAd
    }

    return similarKolsByKol
  })()

  const needMockData = isLocked || isPublicDetailPage

  return needMockData
    ? {
        ...similarKols,
        data: similarKols.data
          ? {
              ...((): ListResource<SimilarKols> => {
                const similarKolsWithMockData = (
                  similarKols.data.data.length > 0
                    ? [similarKols.data.data[0], ...mockSimilarKolList]
                    : []
                ).slice(0, per_page)

                return {
                  ...similarKols.data,
                  data: similarKolsWithMockData,
                  total: similarKolsWithMockData.length,
                  totalPage: 1,
                }
              })(),
            }
          : undefined,
      }
    : similarKols
}

export default useSimilarKols
